/**
 * Collection date & time picker for click & collect.
 *
 * The date and time options themselves are rendered by
 * snippets/pickup-picker.liquid, so the picker still works with JavaScript off.
 * This file adds four things on top:
 *   1. upgrades the date <select> into a month-grid calendar, with closed days
 *      (Krumb is shut Mon/Tue) shown but greyed out rather than missing
 *   2. removes time slots that have already passed when "today" is selected
 *   3. saves the choice to the cart immediately, so it survives to checkout
 *   4. stops checkout while a required slot is empty
 *
 * The calendar never invents its own availability rules. The bookable days are
 * exactly the <option> values Liquid produced, so every theme setting
 * (open days, same-day cut-off, max days ahead) and the per-product
 * custom.lead_time_hours metafield are honoured by construction. Everything the
 * calendar knows on top of that -- which weekday is a closed day, how far ahead
 * booking runs -- comes from data attributes on the wrapper.
 *
 * Choosing a day writes into the original <select> and fires a real `change`
 * event, so the existing time filtering, cart saving and checkout gating run
 * untouched. The <select> itself is never replaced or removed: it is moved into
 * a collapsed "choose from a list" disclosure, so it keeps its `form`
 * attribute, keeps submitting, and stays available to assistive technology.
 *
 * Uses delegated listeners throughout, because Dawn re-renders the cart drawer
 * on every cart update and would otherwise throw away our handlers.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-krumb-pickup]';
  var LAST_ORDER_BUFFER_MINUTES = 15; // don't offer a slot that's about to start
  var WEEK_START = 1; // Monday-first, this is the UK

  var MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  var DAY_NAMES = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  /* Micro-copy. The two customer-facing sentences (the closed-days note and the
     list toggle) are theme settings and arrive on data attributes; these are the
     interface words. */
  var TEXT = {
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    listToggle: 'Choose a date from a list instead',
    legendToday: 'Today',
    legendSelected: 'Chosen',
    legendClosed: 'Unavailable',
    isToday: 'today',
    isSelected: 'selected',
    closed: 'closed',
    past: 'in the past',
    tooSoon: 'too soon for what is in your basket',
    tooFar: 'not open for booking yet',
    unavailable: 'not available'
  };

  var supported =
    typeof window.MutationObserver !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function' &&
    typeof Element !== 'undefined' &&
    Element.prototype &&
    typeof Element.prototype.closest === 'function';

  /* Set while we are writing into a <select> ourselves, so the shared change
     handler doesn't re-render the calendar out from under the click handler. */
  var writingTo = null;

  /* ---------------------------------------------------------------- helpers */

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /** Local-midnight ISO date. Never toISOString(), which would shift by UTC. */
  function iso(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function parseIso(value) {
    var parts = String(value || '').split('-');
    if (parts.length !== 3) return null;
    var date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return isNaN(date.getTime()) ? null : date;
  }

  function addDays(date, count) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
  }

  function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  function addMonths(date, count) {
    return new Date(date.getFullYear(), date.getMonth() + count, 1);
  }

  /** Same day-of-month in another month, clamped to that month's length. */
  function shiftMonth(date, count) {
    var target = new Date(date.getFullYear(), date.getMonth() + count, 1);
    var lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
  }

  function locale() {
    return document.documentElement.getAttribute('lang') || 'en-GB';
  }

  function formatMonth(date) {
    try {
      return date.toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
    } catch (error) {
      return MONTH_NAMES[date.getMonth()] + ' ' + date.getFullYear();
    }
  }

  function formatFull(date) {
    try {
      return date.toLocaleDateString(locale(), {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch (error) {
      return DAY_NAMES[date.getDay()] + ' ' + date.getDate() + ' ' +
        MONTH_NAMES[date.getMonth()] + ' ' + date.getFullYear();
    }
  }

  function formatWeekday(dayIndex, style) {
    // 2024-01-07 was a Sunday, so +dayIndex lands on the weekday we want.
    var sample = new Date(2024, 0, 7 + dayIndex);
    try {
      return sample.toLocaleDateString(locale(), { weekday: style });
    } catch (error) {
      return style === 'long' ? DAY_NAMES[dayIndex] : DAY_NAMES[dayIndex].slice(0, 3);
    }
  }

  function minutesNow() {
    var now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }

  function pickers() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function fieldsOf(picker) {
    return {
      date: picker.querySelector('[data-krumb-pickup-date]'),
      time: picker.querySelector('[data-krumb-pickup-time]'),
      error: picker.querySelector('[data-krumb-pickup-error]')
    };
  }

  /* --------------------------------------------------- existing behaviour */

  /** Grey out times that have already gone, but only when today is chosen. */
  function refreshTimes(picker) {
    var f = fieldsOf(picker);
    if (!f.date || !f.time) return;

    var isToday = f.date.value && f.date.value === picker.dataset.today;
    var cutoff = minutesNow() + LAST_ORDER_BUFFER_MINUTES;
    var selectedGone = false;

    Array.prototype.forEach.call(f.time.options, function (option) {
      if (!option.value) return;
      var slot = parseInt(option.dataset.minutes, 10);
      var past = isToday && !isNaN(slot) && slot < cutoff;
      option.disabled = past;
      option.hidden = past;
      if (past && option.selected) selectedGone = true;
    });

    if (selectedGone) {
      f.time.value = '';
      save(picker);
    }
  }

  function showError(picker, message) {
    var f = fieldsOf(picker);
    if (!f.error) return;
    if (message) {
      f.error.textContent = message;
      f.error.hidden = false;
      if (f.date && !f.date.value) f.date.setAttribute('aria-invalid', 'true');
      if (f.time && !f.time.value) f.time.setAttribute('aria-invalid', 'true');
      // A blocked checkout whose explanation sits below the fold reads as a
      // dead button. Bring the picker and its message into view, wherever
      // the customer was when they tapped Checkout.
      try {
        var scrollErrIntoView = function () {
          (f.error || picker).scrollIntoView({ block: 'center' });
        };
        scrollErrIntoView();
        // Run once more after the guard's focus/detail-opening has settled,
        // so nothing later in the same interaction steals the viewport.
        window.setTimeout(scrollErrIntoView, 120);
        picker.classList.remove('krumb-pickup--attention');
        void picker.offsetWidth;
        picker.classList.add('krumb-pickup--attention');
      } catch (scrollError) { /* non-fatal */ }
    } else {
      f.error.hidden = true;
      f.error.textContent = '';
      if (f.date) f.date.removeAttribute('aria-invalid');
      if (f.time) f.time.removeAttribute('aria-invalid');
    }
  }

  function isComplete(picker) {
    var f = fieldsOf(picker);
    if (picker.dataset.required !== 'true') return true;
    return Boolean(f.date && f.date.value && f.time && f.time.value);
  }

  /** Persist to the cart so the choice survives even if the form isn't submitted. */
  function save(picker) {
    var f = fieldsOf(picker);
    var body = {
      attributes: {
        'Pickup date': (f.date && f.date.value) || '',
        'Pickup time': (f.time && f.time.value) || ''
      }
    };
    // The old Delivery & Pickup app tagged orders with this attribute and the
    // shop filters on it. Keep writing it once a date is chosen, so nothing
    // changes for staff when the app is uninstalled.
    if (body.attributes['Pickup date']) {
      body.attributes['Order Type'] = 'Store Pickup';
      // Staff read these on the admin order screen, where the ISO date the
      // form needs (2026-09-03) is easy to misread. Store a spoken-English
      // copy alongside it, like the old app's "Delivery Day" did.
      var spoken = new Date(body.attributes['Pickup date'] + 'T12:00:00');
      if (!isNaN(spoken)) {
        body.attributes['Pickup day'] = spoken.toLocaleDateString('en-GB', {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
      }
    }
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/update.js'
        : '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).catch(function () {
      /* Non-fatal: the selects are inside the cart form, so the values still
         submit with checkout even if this call fails. */
    });
  }

  /** The "Collecting: Friday 4 September 2026 at 15:45" line under the
      fields - the customer's confirmation of what they picked, since
      Shopify's checkout page never shows cart attributes. */
  function updateSummary(picker) {
    var el = picker.querySelector('[data-krumb-pickup-summary]');
    if (!el) return;
    var f = fieldsOf(picker);
    var date = f.date && f.date.value ? parseIso(f.date.value) : null;
    var time = f.time && f.time.value;
    if (date && time) {
      el.textContent = 'Collecting: ' + DAY_NAMES[date.getDay()] + ' ' +
        date.getDate() + ' ' + MONTH_NAMES[date.getMonth()] + ' ' +
        date.getFullYear() + ' at ' + time;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  /* ------------------------------------------------------------- calendar */

  function calendarOf(picker) {
    return picker.querySelector('[data-krumb-calendar]');
  }

  /** The days Liquid decided are bookable. This is the only source of truth. */
  function availableDates(select) {
    return Array.prototype.filter
      .call(select.options, function (option) {
        return option.value && !option.disabled;
      })
      .map(function (option) {
        return option.value;
      })
      .sort();
  }

  function openDayFlags(picker) {
    var flags = {};
    String(picker.getAttribute('data-open-days') || '')
      .split(',')
      .forEach(function (part) {
        var day = parseInt(part, 10);
        if (!isNaN(day)) flags[day] = true;
      });
    return flags;
  }

  function rangeStart(picker) {
    return parseIso(picker.getAttribute('data-today')) || new Date();
  }

  function rangeEnd(picker) {
    var maxDays = parseInt(picker.getAttribute('data-max-days'), 10);
    return addDays(rangeStart(picker), isNaN(maxDays) ? 21 : maxDays);
  }

  function chevron(direction) {
    var path = direction < 0 ? 'M8 1 2 8l6 7' : 'M2 1l6 7-6 7';
    return '<svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true" focusable="false">' +
      '<path d="' + path + '" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function legendKey(modifier, label) {
    return '<span class="krumb-cal__key">' +
      '<span class="krumb-cal__swatch krumb-cal__swatch--' + modifier + '" aria-hidden="true"></span>' +
      label + '</span>';
  }

  /** Build the calendar shell once. Only the day cells are re-rendered later. */
  function buildShell(picker, suffix, title) {
    var closedNote = picker.getAttribute('data-closed-note') || '';

    var head = '';
    for (var column = 0; column < 7; column++) {
      var dayIndex = (WEEK_START + column) % 7;
      head += '<th scope="col" class="krumb-cal__weekday">' +
        '<span aria-hidden="true">' + formatWeekday(dayIndex, 'short') + '</span>' +
        '<span class="krumb-cal__sr">' + formatWeekday(dayIndex, 'long') + '</span>' +
        '</th>';
    }

    var wrapper = document.createElement('div');
    wrapper.className = 'krumb-cal';
    wrapper.setAttribute('data-krumb-calendar', '');
    wrapper.innerHTML =
      '<span class="krumb-cal__title" id="KrumbCalTitle-' + suffix + '"></span>' +
      '<div class="krumb-cal__head">' +
        '<button type="button" class="krumb-cal__nav" data-cal-step="-1" aria-label="' +
          TEXT.prevMonth + '">' + chevron(-1) + '</button>' +
        '<span class="krumb-cal__month" id="KrumbCalMonth-' + suffix + '" ' +
          'data-cal-month aria-live="polite" aria-atomic="true"></span>' +
        '<button type="button" class="krumb-cal__nav" data-cal-step="1" aria-label="' +
          TEXT.nextMonth + '">' + chevron(1) + '</button>' +
      '</div>' +
      '<table class="krumb-cal__grid" role="grid" data-cal-grid ' +
        'aria-labelledby="KrumbCalTitle-' + suffix + ' KrumbCalMonth-' + suffix + '">' +
        '<thead><tr class="krumb-cal__weekdays">' + head + '</tr></thead>' +
        '<tbody data-cal-body></tbody>' +
      '</table>' +
      '<p class="krumb-cal__legend">' +
        legendKey('today', TEXT.legendToday) +
        legendKey('selected', TEXT.legendSelected) +
        legendKey('closed', TEXT.legendClosed) +
      '</p>' +
      (closedNote ? '<p class="krumb-cal__note"></p>' : '') +
      '<p class="krumb-cal__status" role="status" aria-live="polite" data-cal-status></p>';

    // textContent, not innerHTML, for anything a merchant typed.
    wrapper.querySelector('.krumb-cal__title').textContent = title;
    if (closedNote) wrapper.querySelector('.krumb-cal__note').textContent = closedNote;

    return wrapper;
  }

  function announce(picker, message) {
    var status = picker.querySelector('[data-cal-status]');
    if (status) status.textContent = message || '';
  }

  /**
   * Draw the visible month. Rebuilds only <tbody>, so the month heading and the
   * two nav buttons keep their focus across a month change.
   */
  function render(picker, focusDate) {
    var blockedDays = {};
    (picker.getAttribute('data-blocked-dates') || '').split(',').forEach(function (d) {
      if (d) blockedDays[d] = true;
    });
    var blockedNote = picker.getAttribute('data-blocked-note') || 'Fully booked';

    var calendar = calendarOf(picker);
    var select = fieldsOf(picker).date;
    if (!calendar || !select) return;

    var start = rangeStart(picker);
    var end = rangeEnd(picker);
    var minMonth = startOfMonth(start);
    var maxMonth = startOfMonth(end);

    var view = parseIso(picker.getAttribute('data-cal-view')) || minMonth;
    view = startOfMonth(view);
    if (view.getTime() < minMonth.getTime()) view = minMonth;
    if (view.getTime() > maxMonth.getTime()) view = maxMonth;
    picker.setAttribute('data-cal-view', iso(view));

    var available = {};
    var dates = availableDates(select);
    dates.forEach(function (value) { available[value] = true; });
    var firstAvailable = dates[0] || null;

    var openDays = openDayFlags(picker);
    var todayKey = picker.getAttribute('data-today');
    var selectedKey = select.value;

    calendar.querySelector('[data-cal-month]').textContent = formatMonth(view);

    var prev = calendar.querySelector('[data-cal-step="-1"]');
    var next = calendar.querySelector('[data-cal-step="1"]');
    prev.disabled = view.getTime() <= minMonth.getTime();
    next.disabled = view.getTime() >= maxMonth.getTime();

    var firstOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
    var lead = (firstOfMonth.getDay() - WEEK_START + 7) % 7;
    var lastDay = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    var rows = Math.ceil((lead + lastDay) / 7);

    // Which single day is in the tab order (roving tabindex).
    var candidates = [
      focusDate,
      picker.getAttribute('data-cal-focus'),
      selectedKey,
      todayKey
    ];
    var focusKey = null;
    for (var c = 0; c < candidates.length; c++) {
      var candidate = candidates[c];
      var parsed = candidate ? parseIso(candidate) : null;
      if (parsed && parsed.getFullYear() === view.getFullYear() &&
          parsed.getMonth() === view.getMonth()) {
        focusKey = candidate;
        break;
      }
    }
    if (!focusKey) {
      for (var a = 0; a < dates.length; a++) {
        var candidateDate = parseIso(dates[a]);
        if (candidateDate && candidateDate.getFullYear() === view.getFullYear() &&
            candidateDate.getMonth() === view.getMonth()) {
          focusKey = dates[a];
          break;
        }
      }
    }
    if (!focusKey) focusKey = iso(firstOfMonth);
    picker.setAttribute('data-cal-focus', focusKey);

    var body = calendar.querySelector('[data-cal-body]');
    var html = '';

    for (var row = 0; row < rows; row++) {
      html += '<tr class="krumb-cal__row" role="row">';
      for (var column = 0; column < 7; column++) {
        var dayNumber = row * 7 + column - lead + 1;

        if (dayNumber < 1 || dayNumber > lastDay) {
          html += '<td class="krumb-cal__cell krumb-cal__cell--blank" role="gridcell"></td>';
          continue;
        }

        var date = new Date(view.getFullYear(), view.getMonth(), dayNumber);
        var key = iso(date);
        var isOpenDay = openDays[date.getDay()] === true;
        var isAvailable = available[key] === true;
        var isToday = key === todayKey;
        var isSelected = Boolean(selectedKey) && key === selectedKey;

        var state = 'available';
        var reason = '';
        if (!isAvailable) {
          // Closed days are tested first, so Mondays and Tuesdays always read
          // as closed -- that consistency is the whole point of the grid.
          if (!isOpenDay) {
            state = 'closed';
            reason = TEXT.closed;
          } else if (blockedDays[key]) {
            // Marked fully booked in Theme settings > Click & collect.
            state = 'full';
            reason = blockedNote;
          } else if (date.getTime() < start.getTime()) {
            state = 'past';
            reason = TEXT.past;
          } else if (date.getTime() > end.getTime()) {
            state = 'far';
            reason = TEXT.tooFar;
          } else if (firstAvailable && key < firstAvailable) {
            state = 'soon';
            reason = TEXT.tooSoon;
          } else {
            state = 'unavailable';
            reason = TEXT.unavailable;
          }
        }

        var label = formatFull(date);
        if (isToday) label += ', ' + TEXT.isToday;
        if (isSelected) label += ', ' + TEXT.isSelected;
        if (reason) label += ', ' + reason;

        var classes = 'krumb-cal__day krumb-cal__day--' + state;
        if (isToday) classes += ' is-today';
        if (isSelected) classes += ' is-selected';

        html += '<td class="krumb-cal__cell" role="gridcell" aria-selected="' +
          (isSelected ? 'true' : 'false') + '">' +
          '<button type="button" class="' + classes + '"' +
          ' data-cal-day data-date="' + key + '"' +
          ' data-reason="' + (reason ? label : '') + '"' +
          ' tabindex="' + (key === focusKey ? '0' : '-1') + '"' +
          (isAvailable ? '' : ' aria-disabled="true"') +
          (isToday ? ' aria-current="date"' : '') +
          ' aria-label="' + label + '">' +
          '<span aria-hidden="true">' + dayNumber + '</span>' +
          '</button></td>';
      }
      html += '</tr>';
    }

    body.innerHTML = html;

    if (focusDate) {
      var target = body.querySelector('[data-date="' + focusDate + '"]');
      if (target) target.focus({ preventScroll: true });
    }
  }

  /** Move the <select> into a disclosure and put a calendar above it. */
  function enhance(picker) {
    if (picker.hasAttribute('data-krumb-calendar-ready')) return;

    var select = fieldsOf(picker).date;
    var field = select && select.closest('.krumb-pickup__field');

    // Nothing bookable (or nothing to enhance): leave the plain select alone.
    if (!select || !field || !availableDates(select).length) {
      picker.setAttribute('data-krumb-calendar-ready', 'skipped');
      return;
    }

    var suffix = picker.getAttribute('data-form') || 'cart';
    var label = field.querySelector('.krumb-pickup__label');
    var title = label ? label.textContent.replace(/\s*\*\s*$/, '').trim() : 'Collection date';

    var calendar = buildShell(picker, suffix, title);

    var details = document.createElement('details');
    details.className = 'krumb-pickup__fallback';
    var summary = document.createElement('summary');
    summary.className = 'krumb-pickup__fallback-summary';
    summary.textContent = picker.getAttribute('data-list-label') || TEXT.listToggle;
    details.appendChild(summary);

    // Everything above is detached; only now do we touch the live DOM.
    field.insertBefore(calendar, field.firstChild);
    if (label) details.appendChild(label);
    details.appendChild(select);
    field.appendChild(details);

    picker.classList.add('krumb-pickup--has-calendar');
    picker.setAttribute('data-krumb-calendar-ready', 'true');

    var opening = select.value ? parseIso(select.value) : rangeStart(picker);
    picker.setAttribute('data-cal-view', iso(startOfMonth(opening || rangeStart(picker))));
    render(picker);
  }

  /** Write a day into the select and let the existing handlers do their work. */
  function choose(picker, key) {
    var select = fieldsOf(picker).date;
    if (!select) return;

    select.value = key;
    if (select.value !== key) return; // the option vanished under us

    writingTo = picker;
    try {
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      writingTo = null;
    }

    picker.setAttribute('data-cal-focus', key);
    announce(picker, '');
    render(picker, key);
  }

  /* ------------------------------------------------------------ listeners */

  document.addEventListener('change', function (event) {
    var field = event.target.closest
      ? event.target.closest('[data-krumb-pickup-date], [data-krumb-pickup-time]')
      : null;
    if (!field) return;
    var picker = field.closest(SELECTOR);
    if (!picker) return;

    if (field.hasAttribute('data-krumb-pickup-date')) {
      refreshTimes(picker);
      // Keep the calendar in step when the list fallback is used. Skipped when
      // the calendar itself made the change, so we don't render twice.
      if (writingTo !== picker && calendarOf(picker)) {
        var chosen = field.value ? parseIso(field.value) : null;
        if (chosen) picker.setAttribute('data-cal-view', iso(startOfMonth(chosen)));
        render(picker);
      }
    }
    if (isComplete(picker)) showError(picker, '');
    updateSummary(picker);
    save(picker);
  });

  /* Calendar clicks: month navigation and day selection. */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var nav = event.target.closest('[data-cal-step]');
    if (nav) {
      var navPicker = nav.closest(SELECTOR);
      if (!navPicker) return;
      var view = parseIso(navPicker.getAttribute('data-cal-view'));
      if (!view) return;
      navPicker.setAttribute('data-cal-view', iso(addMonths(view, parseInt(nav.getAttribute('data-cal-step'), 10))));
      announce(navPicker, '');
      render(navPicker);
      // Don't strand focus on a button that has just been disabled.
      if (nav.disabled) {
        var sibling = calendarOf(navPicker).querySelector('[data-cal-step]:not([disabled])');
        if (sibling) sibling.focus();
      }
      return;
    }

    var day = event.target.closest('[data-cal-day]');
    if (!day) return;
    var picker = day.closest(SELECTOR);
    if (!picker) return;

    if (day.getAttribute('aria-disabled') === 'true') {
      announce(picker, day.getAttribute('data-reason') || '');
      return;
    }
    choose(picker, day.getAttribute('data-date'));
  });

  /* Grid keyboard navigation. Enter and Space are the button default. */
  document.addEventListener('keydown', function (event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!event.target.closest) return;

    var day = event.target.closest('[data-cal-day]');
    if (!day) return;
    var picker = day.closest(SELECTOR);
    if (!picker) return;

    var from = parseIso(day.getAttribute('data-date'));
    if (!from) return;

    var offset = (from.getDay() - WEEK_START + 7) % 7;
    var to = null;

    switch (event.key) {
      case 'ArrowLeft': case 'Left': to = addDays(from, -1); break;
      case 'ArrowRight': case 'Right': to = addDays(from, 1); break;
      case 'ArrowUp': case 'Up': to = addDays(from, -7); break;
      case 'ArrowDown': case 'Down': to = addDays(from, 7); break;
      case 'Home': to = addDays(from, -offset); break;
      case 'End': to = addDays(from, 6 - offset); break;
      case 'PageUp': to = shiftMonth(from, event.shiftKey ? -12 : -1); break;
      case 'PageDown': to = shiftMonth(from, event.shiftKey ? 12 : 1); break;
      default: return;
    }

    event.preventDefault();

    var start = rangeStart(picker);
    var end = rangeEnd(picker);
    if (to.getTime() < start.getTime()) to = start;
    if (to.getTime() > end.getTime()) to = end;

    picker.setAttribute('data-cal-view', iso(startOfMonth(to)));
    render(picker, iso(to));
  });

  /**
   * A required <select> that sits inside a closed <details> cannot be focused,
   * so the browser would refuse to submit with no visible reason. Open the
   * disclosure first. `invalid` doesn't bubble, hence capture.
   */
  document.addEventListener('invalid', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('[data-krumb-pickup-date]')) return;
    var details = target.closest('details');
    if (details) details.open = true;
  }, true);

  /** Block checkout while a required slot is missing. */
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest ? event.target.closest('[name="checkout"]') : null;
      if (!button) return;

      var formId = button.getAttribute('form');
      var picker = pickers().filter(function (candidate) {
        return !formId || candidate.dataset.form === formId;
      })[0] || pickers()[0];

      if (!picker || isComplete(picker)) return;

      event.preventDefault();
      event.stopPropagation();
      showError(
        picker,
        picker.dataset.errorMessage ||
          'Please choose a collection date and time before checking out.'
      );

      var f = fieldsOf(picker);
      var needsDate = f.date && !f.date.value;
      var focusTarget = null;

      if (needsDate) {
        // Prefer the calendar when it's there; fall back to the select.
        focusTarget = picker.querySelector('[data-cal-day][tabindex="0"]') || f.date;
        if (focusTarget === f.date) {
          var details = f.date.closest('details');
          if (details) details.open = true;
        }
      } else {
        focusTarget = f.time;
      }

      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
        if (focusTarget.scrollIntoView) {
          focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    true // capture, so we run before Dawn's own submit handling
  );

  /* ----------------------------------------------------------------- boot */

  function init() {
    pickers().forEach(function (picker) {
      refreshTimes(picker);
      if (!supported) return;
      try {
        enhance(picker);
      } catch (error) {
        // Any failure leaves the native select exactly as Liquid rendered it.
        picker.setAttribute('data-krumb-calendar-ready', 'skipped');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Dawn replaces cart markup wholesale after updates, which throws away the
  // calendar we injected. Re-enhance whenever an un-enhanced picker appears.
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      init();
    });
  }

  if (supported) {
    new window.MutationObserver(function () {
      if (document.querySelector(SELECTOR + ':not([data-krumb-calendar-ready])')) schedule();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('cart:refresh', init);
  document.addEventListener('cart:updated', init);

  // Dawn's own pub/sub, when it is available. Both are script-global rather
  // than properties of window, so they are read as bare identifiers.
  try {
    if (typeof subscribe === 'function') {
      subscribe(
        typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update',
        schedule
      );
    }
  } catch (error) {
    /* pubsub.js not loaded on this page; the observer above covers us. */
  }
})();
